import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const CookiePolicy = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Cookie Policy | Sampler</title>
                <meta name="description" content="Sampler Cookie Policy — Ritson Portfolio Ltd." />
            </Helmet>

            {/* Sticky header */}
            <div className="sticky top-0 z-10 border-b border-purple-100 bg-white/80 backdrop-blur-md">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 hover:text-[#8B5CF6] -ml-2" data-testid="legal-back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                    <img src="/sampler-logo.png" alt="Sampler" className="h-8 w-auto" />
                </div>
            </div>

            {/* Document body */}
            <div className="max-w-3xl mx-auto px-4 py-12">
                <div className="bg-white rounded-3xl border border-purple-200/50 shadow-sm p-8 md:p-12">

                    {/* Document header */}
                    <div className="mb-10 pb-8 border-b border-purple-100">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Legal
                        </span>
                        <h1 className="text-3xl font-bold mb-5 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Cookie Policy
                        </h1>
                        <div className="text-sm text-slate-500 space-y-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            <p>Ritson Portfolio Ltd (trading as Sampler) | Company Number: 17242741 | Registered in England and Wales</p>
                            <p>Registered Office: Ritson Haven, Ritson Road, London, E8 1DE | ICO Registration: ZC195328</p>
                            <p className="font-semibold text-slate-700 mt-2">Effective Date: 13 July 2026 | sampler.org.uk</p>
                        </div>
                    </div>

                    <div className="space-y-10 text-slate-700" style={{ fontFamily: 'Barlow, sans-serif' }}>

                        {/* 1 */}
                        <Section title="1. What Are Cookies">
                            <Clause>Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work, improve efficiency, and provide information to site owners. Cookies cannot run programmes or deliver viruses to your device.</Clause>
                        </Section>

                        {/* 2 */}
                        <Section title="2. How We Use Cookies">
                            <Clause>Sampler uses cookies to:</Clause>
                            <BulletList items={[
                                'Ensure the platform functions correctly and securely',
                                'Remember your preferences and session state',
                                'Understand how users navigate the platform so we can improve it',
                                'Measure the effectiveness of our marketing activity',
                            ]} />
                            <Clause>We do not use cookies to collect sensitive personal data, serve third-party advertising, or sell your data to any third party.</Clause>
                        </Section>

                        {/* 3 */}
                        <Section title="3. Types of Cookies We Use">

                            <SubHeading>3.1 Strictly Necessary Cookies</SubHeading>
                            <Clause>These cookies are essential for the platform to function. They enable core features such as user authentication, session management, and security. You cannot opt out of these cookies as the platform cannot operate without them.</Clause>
                            <Clause>Examples:</Clause>
                            <BulletList items={[
                                'Session tokens (keeping you logged in)',
                                'CSRF protection tokens',
                                'Load balancing cookies',
                            ]} />

                            <SubHeading>3.2 Functional Cookies</SubHeading>
                            <Clause>These cookies allow us to remember choices you have made (such as your sector preference or role type) and provide a more personalised experience. They do not track your activity across other websites.</Clause>

                            <SubHeading>3.3 Analytics Cookies</SubHeading>
                            <Clause>These cookies help us understand how visitors interact with the platform — which pages are visited, how long users spend on each section, and where users drop off. This information is used in aggregate and does not identify you personally. We use privacy-respecting analytics tools configured to anonymise IP addresses and not share data with third parties for advertising purposes.</Clause>

                            <SubHeading>3.4 Marketing and Tracking Cookies</SubHeading>
                            <Clause>We may use cookies to measure the effectiveness of campaigns run on platforms such as LinkedIn, Meta, or Google. These cookies help us understand whether a user who clicked an advert subsequently registered on the platform. You can opt out of these cookies via our cookie consent banner or through your browser settings. Opting out will not affect your ability to use the platform.</Clause>
                        </Section>

                        {/* 4 */}
                        <Section title="4. Third-Party Cookies">
                            <Clause>Some cookies on our platform are set by third-party services we use. These include:</Clause>
                            <BulletList items={[
                                'Stripe — payment processing (strictly necessary for transaction completion)',
                                'Analytics providers — aggregated, anonymised usage data',
                                'LinkedIn / Meta / Google — campaign attribution (only where you have consented)',
                            ]} />
                            <Clause>We do not control third-party cookies. Please refer to the relevant third party's privacy and cookie policy for further information.</Clause>
                        </Section>

                        {/* 5 */}
                        <Section title="5. Your Cookie Choices">
                            <Clause>When you first visit sampler.org.uk, you will be presented with a cookie consent banner. You can:</Clause>
                            <BulletList items={[
                                'Accept all cookies',
                                'Accept only strictly necessary cookies',
                                'Customise your preferences by category',
                            ]} />
                            <Clause>You can change or withdraw your consent at any time by clicking the "Cookie Settings" link in the footer of any page. Please note that withdrawing consent does not affect the lawfulness of any processing carried out before you withdrew it.</Clause>

                            <SubHeading>5.1 Browser-Level Controls</SubHeading>
                            <Clause>You can also control cookies through your browser settings. Most browsers allow you to:</Clause>
                            <BulletList items={[
                                'View cookies that have been set',
                                'Block cookies from specific websites',
                                'Block all third-party cookies',
                                'Delete all cookies when you close the browser',
                            ]} />
                            <Clause>Please refer to your browser's help documentation for instructions. Note that blocking all cookies may affect the functionality of the platform.</Clause>
                        </Section>

                        {/* 6 */}
                        <Section title="6. How Long Do Cookies Last">
                            <Clause>Session cookies are deleted when you close your browser. Persistent cookies remain on your device for a set period or until you delete them. The table below summarises our cookie retention periods:</Clause>
                            <div className="mt-4 overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-purple-50">
                                            <th className="text-left px-4 py-3 font-semibold text-slate-700 border border-purple-100 rounded-tl-lg">Cookie Type</th>
                                            <th className="text-left px-4 py-3 font-semibold text-slate-700 border border-purple-100 rounded-tr-lg">Retention Period</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ['Strictly Necessary', 'Session duration or up to 30 days'],
                                            ['Functional', 'Up to 12 months'],
                                            ['Analytics', 'Up to 13 months'],
                                            ['Marketing / Attribution', 'Up to 90 days'],
                                        ].map(([type, period], i) => (
                                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                                <td className="px-4 py-3 border border-purple-100 text-slate-600">{type}</td>
                                                <td className="px-4 py-3 border border-purple-100 text-slate-600">{period}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Section>

                        {/* 7 */}
                        <Section title="7. Legal Basis for Cookie Use">
                            <Clause>Under UK GDPR and the Privacy and Electronic Communications Regulations 2003 (PECR):</Clause>
                            <BulletList items={[
                                'Strictly necessary cookies are used on the basis of legitimate interest and platform functionality — no consent is required.',
                                'All other cookies are used only with your explicit consent, which you provide via our cookie consent banner.',
                            ]} />
                        </Section>

                        {/* 8 */}
                        <Section title="8. Changes to This Policy">
                            <Clause>We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our services. We will notify you of material changes via the platform or by email. The "Effective Date" at the top of this page reflects the most recent version.</Clause>
                        </Section>

                        {/* 9 */}
                        <Section title="9. Contact Us">
                            <Clause>If you have questions about our use of cookies, please contact us at:</Clause>
                            <div className="mt-3 ml-4 text-sm space-y-1">
                                <p><strong>Sampler</strong></p>
                                <p>Email: <a href="mailto:privacy@sampler.org.uk" className="text-[#8B5CF6] hover:underline">privacy@sampler.org.uk</a></p>
                                <p>Website: <a href="https://sampler.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline">sampler.org.uk</a></p>
                            </div>
                            <Clause>If you are not satisfied with our response, you have the right to complain to the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline">ico.org.uk</a>.</Clause>
                        </Section>

                    </div>
                </div>
            </div>
        </div>
    );
};

/* ── Shared sub-components ── */

const Section = ({ title, children }) => (
    <section>
        <h2 className="text-sm font-bold mb-4 text-[#0A0A0A] uppercase tracking-widest" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {title}
        </h2>
        <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </section>
);

const SubHeading = ({ children }) => (
    <h3 className="font-semibold text-slate-800 mt-4 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>{children}</h3>
);

const Clause = ({ children }) => (
    <p>{children}</p>
);

const BulletList = ({ items }) => (
    <ul className="list-disc ml-5 space-y-1.5">
        {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
);

export default CookiePolicy;
